import { ReactNode, useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import { ContactType } from "../../../models/Contact";
import { IAddressFields, IAddressLookupResult } from "../../../models/IAddressFields";
import classNames from "../../../utils/classNames";
import getAddress from "../../../utils/getAddress";
import Modal from "../../../layouts/Modal";
import Card from "../Card/Card";
import { ButtonInput } from "../Inputs/ButtonInput";
import { TextInput } from "../Inputs/TextInput";

export type LocationFormValues = {
  fullname: string;
  contactType: ContactType;
  telephone1: string;
  telephone2: string;
  email: string;
} & IAddressFields;

export type LocationFormAddress = IAddressLookupResult;

interface LocationFormProps {
  initialValues: LocationFormValues;
  location: LocationFormAddress | null;
  onSubmit: (data: LocationFormValues) => void;
  onGetAddresses: (postcode: string) => Promise<LocationFormAddress[]>;
  onAddressLookupEmpty: () => void;
  onAddressLookupError: () => void;
  children?: ReactNode;
  emptyMessage?: string;
  heading?: string;
  title?: string;
  variant?: "embedded" | "standalone";
  isSubmitting?: boolean;
}

export default function LocationForm({
  initialValues,
  location,
  onSubmit,
  onGetAddresses,
  onAddressLookupEmpty,
  onAddressLookupError,
  children = null,
  emptyMessage = "No location is linked to this lead.",
  heading = "Vehicle pickup",
  title = "Vehicle Pickup Details",
  variant = "standalone",
  isSubmitting = false,
}: LocationFormProps) {
  const [addresses, setAddresses] = useState<LocationFormAddress[]>([]);
  const [addressesOpen, setAddressesOpen] = useState(false);
  const { register, getValues, setValue, watch, handleSubmit, reset } = useForm<LocationFormValues>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const handlePostcode = async () => {
    const postcode = getValues("postcode");
    if (!postcode) return;

    try {
      const addresses = await onGetAddresses(postcode);

      if (addresses.length !== 0) {
        setAddresses(
          addresses.sort((a, b) => {
            return a.line1 ? a.line1.localeCompare(b.line1 || "", "en", { numeric: true }) : +1;
          })
        );
        setAddressesOpen(true);
      } else {
        onAddressLookupEmpty();
      }
    } catch {
      onAddressLookupError();
    }
  };

  const handleFormSubmit = (data: LocationFormValues) => {
    onSubmit(data);
    reset(data);
  };

  const currentValues = watch();
  const line1 = currentValues.line1;

  const addressForm = (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-2">
      <div className="grid grid-cols-1 gap-1">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <TextInput label="Postcode" register={register} registerName="postcode" rounded />
          </div>
          <ButtonInput
            classes="shrink-0 sm:mb-0"
            label="Get Addresses"
            isSubmit={false}
            onClick={() => handlePostcode()}
          />
        </div>
        <div>
          <TextInput
            label="Address Line 1"
            register={register}
            registerName="line1"
            customClasses={line1 ? "" : "text-red-600 border-red-600"}
            rounded
          />
          {!line1 && <p className="pt-2 text-sm text-red-500">Please enter an address line 1</p>}
        </div>
        <TextInput label="Address Line 2" register={register} registerName="line2" rounded />
        <TextInput label="Address Line 3" register={register} registerName="line3" rounded />
        <TextInput label="Address Line 4" register={register} registerName="line4" rounded />
        <TextInput label="Locality" register={register} registerName="locality" rounded />
        <TextInput label="Town" register={register} registerName="town" rounded />
        <TextInput label="County" register={register} registerName="county" rounded />
      </div>

      <div>
        <dt className="text-sm font-medium text-gray-500">Full Address</dt>
        <dd className="mt-1 text-sm text-gray-900">{getAddress(currentValues)}</dd>
      </div>

      <ButtonInput classes={null} label="Update" isSubmit onClick={null} isLoading={isSubmitting} />
    </form>
  );

  const inner = (
    <>
      {variant === "embedded" && (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{heading}</h3>
      )}

      {!location ? (
        <p className="text-sm text-gray-600">{emptyMessage}</p>
      ) : (
        <>
          {addressForm}
          {children}
        </>
      )}
    </>
  );

  const embeddedShell = (
    <div
      className={classNames(
        "min-w-0 space-y-2 rounded-lg",
        location && !line1 && "bg-red-50"
      )}
    >
      {inner}
    </div>
  );

  const standaloneShell = (
    <Card
      title={title}
      bodyClassName={classNames("p-4 space-y-6", location && !line1 && "bg-red-100")}
      collapsible
    >
      {inner}
    </Card>
  );

  return (
    <>
      <Modal open={addressesOpen} setOpen={setAddressesOpen} width="max-w-4xl">
        <div className="flex max-h-[80vh] w-full flex-col overflow-auto">
          {addresses.length > 0 &&
            addresses.map((address) => (
              <div
                key={address.id || getAddress(address)}
                className="flex w-full cursor-pointer flex-col border-b border-gray-200 p-4 hover:bg-gray-100"
                onClick={() => {
                  setValue("line1", address.line1);
                  setValue("line2", address.line2);
                  setValue("line3", address.line3);
                  setValue("line4", address.line4);
                  setValue("locality", address.locality);
                  setValue("town", address.town);
                  setValue("county", address.county);
                  setValue("postcode", address.postcode);
                  setAddressesOpen(false);
                }}
              >
                {getAddress(address)}
              </div>
            ))}
        </div>
      </Modal>

      {variant === "embedded" ? embeddedShell : standaloneShell}
    </>
  );
}
